import { differenceInMinutes, parseISO } from "date-fns";

import {
  mentionKey,
  mentionsAgent,
} from "@hightouch/lib/llm/asset-comments/mention-tokens";
import {
  AGENT_NAME,
  AssetCommentStatuses,
  isResolvedAssetCommentThreadStatus,
  parsePointCommentAnchor,
} from "@hightouch/lib/llm/asset-comments/types";
import type {
  AssetCommentFragment,
  AssetCommentMentionInput,
} from "src/graphql";
import { formatRelativeShort } from "src/utils/time";

import type { CommentAuthor } from "./comment-item";
import type { ThreadComment } from "./comment-thread-card";
import type { MentionOption } from "./mention-options-list";
import type { RelevantUsers } from "./use-relevant-asset-users";

export type CommentFilters = {
  /** Resolved threads are hidden by default — the rail is a to-do list. */
  showResolved: boolean;
  /** Threads the viewer has commented in, including ones they started. */
  onlyMine: boolean;
  /** Threads with something the viewer hasn't caught up on. */
  onlyUnread: boolean;
};

export const DEFAULT_COMMENT_FILTERS: CommentFilters = {
  showResolved: false,
  onlyMine: false,
  onlyUnread: false,
};

/**
 * The shape {@link isThreadUnread} needs. Select permissions scope `reads` to
 * the session user, so an empty array means they've never read the thread.
 */
export type ReadableThread = {
  last_comment_at?: string | null;
  reads: readonly {
    last_read_comment_at: string;
    manually_marked_unread_at?: string | null;
  }[];
};

/**
 * The shape `filterThreads` needs from a thread, regardless of what the thread
 * hangs off. Each subject's query selects a superset of this, so a concrete
 * thread type from any of them satisfies it.
 */
export type FilterableThread = ReadableThread & {
  current_status: string;
  comments: readonly { author_user_id?: number | string | null }[];
};

/**
 * Whether a thread is settled, from the row every comment surface already has.
 *
 * Thin, but it is the one place the app names the status: the rail, its cards,
 * the canvas pins and the activity log all ask this, and four inline
 * comparisons are four places to miss when the vocabulary moves.
 */
export function isThreadResolved(thread: { current_status: string }): boolean {
  return isResolvedAssetCommentThreadStatus(thread.current_status);
}

/**
 * Whether a thread shows an unread marker: marked by hand, never opened, or a
 * comment newer than the watermark. A thread with nothing visible is read —
 * there is nothing to have missed.
 *
 * `joinedAt` floors the never-opened case. Without it a new arrival finds every
 * thread in the workspace marked unread, none of which is news to them.
 */
export function isThreadUnread(
  thread: ReadableThread,
  joinedAt?: string | null,
): boolean {
  const read = thread.reads?.[0];

  if (read?.manually_marked_unread_at) {
    return true;
  }

  if (!read) {
    return isAfter(thread.last_comment_at, joinedAt);
  }

  if (!thread.last_comment_at) {
    return false;
  }

  return new Date(thread.last_comment_at) > new Date(read.last_read_comment_at);
}

/** Whether `at` is later than `floor`, with a missing floor letting it through. */
function isAfter(
  at: string | null | undefined,
  floor: string | null | undefined,
): boolean {
  if (!at) {
    return true;
  }

  return !floor || new Date(at) > new Date(floor);
}

/**
 * Whether the viewer marked this thread unread by hand. Distinct from being
 * unread: the watermark may already be current, so callers can't tell from it
 * alone whether there's an override left to clear.
 */
export function isManuallyMarkedUnread(thread: ReadableThread): boolean {
  return Boolean(thread.reads?.[0]?.manually_marked_unread_at);
}

/**
 * How long ago a comment was left, abbreviated: "4m ago", "22h ago".
 *
 * Local rather than `formatRelativeShort`, which calls everything under an hour
 * "Just now" — in a conversation a 40-minute-old reply reading as brand new is
 * the difference between someone being here and not. Past the first hour it
 * defers to the shared helper, so the buckets above stay consistent with the
 * rest of the app.
 */
export function formatCommentAge(timestamp: string): string {
  const minutes = differenceInMinutes(new Date(), parseISO(timestamp));

  if (Number.isNaN(minutes) || minutes >= 60) {
    return formatRelativeShort(timestamp);
  }

  return minutes < 1 ? "Just now" : `${minutes}m ago`;
}

/** Null unless the thread is anchored to a moment in a video. */
export function commentAnchorSeconds(anchor: unknown): number | null {
  return parsePointCommentAnchor(anchor)?.t ?? null;
}

/** A video offset as `M:SS`. Minutes aren't capped at 60; ads don't run that long. */
export function formatCommentTimestamp(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));

  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export function formatCommentAnchorTimestamp(
  anchor: unknown,
): string | undefined {
  const seconds = commentAnchorSeconds(anchor);

  return seconds == null ? undefined : formatCommentTimestamp(seconds);
}

/** Maps a picked mention onto the mutation's one-of input. */
export function toMentionInput(
  mention: MentionOption,
): AssetCommentMentionInput {
  switch (mention.kind) {
    case "user":
      return { userId: mention.userId };
    case "reviewAgent":
      return { reviewAgentId: mention.reviewAgentId };
    case "agent":
      return { agent: true };
  }
}

/**
 * Current display names for a comment's mention targets, keyed the same way the
 * body's tokens are.
 *
 * The name is read from the joined row rather than from the token in the body:
 * the token preserves whatever the target was called when the comment was
 * written, so someone renamed since then renders under the name they go by now.
 */
export function toMentionNames(
  mentions: AssetCommentFragment["mentions"],
  relevantUsers: RelevantUsers,
): Map<string, string> {
  return new Map(
    mentions.flatMap((mention) => {
      const userName =
        mention.mentioned_user?.name ??
        (mention.mentioned_user_id == null
          ? undefined
          : relevantUsers.get(Number(mention.mentioned_user_id))?.name);

      if (userName && mention.mentioned_user_id != null) {
        return [
          [
            mentionKey({
              kind: "user",
              userId: Number(mention.mentioned_user_id),
            }),
            userName,
          ] as const,
        ];
      }

      if (mention.mentioned_review_agent?.name) {
        return [
          [
            mentionKey({
              kind: "reviewAgent",
              reviewAgentId: mention.mentioned_review_agent.id,
            }),
            mention.mentioned_review_agent.name,
          ] as const,
        ];
      }

      // A target neither source can name contributes nothing; the body's own
      // label stands in. That includes Hightouch's agent, which has no row.
      return [];
    }),
  );
}

/** Structural, so any subject's thread query satisfies it. */
export type AgentParticipantThread = {
  comments: readonly {
    author_review_agent_id?: string | null;
    author_llm_session_id?: string | null;
    body?: string | null;
    mentions?: readonly { mentioned_review_agent_id?: string | null }[];
  }[];
};

/**
 * Whether a review agent is part of this thread.
 *
 * Never expires. An agent answers later comments without being named again, so
 * the mention that invited it is the only one the thread will ever carry, and
 * anything keyed on a recent tag goes quiet during the back-and-forth this is
 * for.
 */
export function hasReviewAgentInThread(
  threads: readonly AgentParticipantThread[] | undefined,
): boolean {
  return (threads ?? []).some((thread) =>
    thread.comments.some(
      (comment) =>
        comment.author_review_agent_id ||
        comment.author_llm_session_id ||
        mentionsAgent(comment.body ?? "") ||
        (comment.mentions ?? []).some(
          (mention) => mention.mentioned_review_agent_id,
        ),
    ),
  );
}

/**
 * Whether an agent owes a reply, or is deciding whether to give one, in any of
 * these threads. The reply row is reserved before the comment's mutation
 * returns, so a surface sees it on the refetch that follows.
 */
export function hasAgentReplyInFlight(
  threads:
    | readonly { comments: readonly { current_status: string }[] }[]
    | undefined,
): boolean {
  return (threads ?? []).some((thread) =>
    thread.comments.some(
      (comment) =>
        comment.current_status === AssetCommentStatuses.Pending ||
        comment.current_status === AssetCommentStatuses.Deciding,
    ),
  );
}

/**
 * Maps comment rows onto what the thread card renders, resolving each author to
 * a person or an agent and deciding who may modify the comment.
 *
 * `canModify` mirrors the service-layer rule (author-only) so the UI doesn't
 * offer an action the backend would reject. It is a display concern only — the
 * resolver enforces it regardless.
 */
export function toThreadComments(
  comments: readonly AssetCommentFragment[],
  currentUserId: number | undefined,
  relevantUsers: RelevantUsers,
): ThreadComment[] {
  return comments.flatMap((comment) => {
    const author = toCommentAuthor(comment, relevantUsers);

    if (!author) {
      return [];
    }

    return [
      {
        id: comment.id,
        author,
        body: comment.body ?? null,
        mentionNames: toMentionNames(comment.mentions, relevantUsers),
        createdAt: comment.created_at,
        status: comment.current_status,
        interactionId: comment.llm_chat_interaction_id ?? null,
        sessionId: comment.llm_chat_interaction?.llm_chat_session_id ?? null,
        canModify:
          author.kind === "user" &&
          currentUserId !== undefined &&
          author.id === currentUserId,
      },
    ];
  });
}

export function toCommentAuthor(
  comment: Pick<
    AssetCommentFragment,
    "author_user" | "author_user_id" | "author_review_agent"
  > &
    Partial<Pick<AssetCommentFragment, "author_llm_session_id">>,
  relevantUsers: RelevantUsers,
): CommentAuthor | null {
  // A file's agent writing as a review agent is shown as that reviewer.
  if (comment.author_review_agent) {
    return {
      kind: "reviewAgent",
      id: comment.author_review_agent.id,
      name: comment.author_review_agent.name,
    };
  }

  if (comment.author_llm_session_id) {
    return { kind: "agent", name: AGENT_NAME };
  }

  // Review agents are readable across the workspace, so a missing agent join means the author is a user or nobody.
  return toUserAuthor(
    comment.author_user,
    comment.author_user_id,
    relevantUsers,
  );
}

/**
 * A user by the joined row, then by the ad's relevant users, then as a
 * placeholder. Null only when the row names no user at all.
 */
export function toUserAuthor(
  user: { id: number | string; name: string } | null | undefined,
  userId: number | string | null | undefined,
  relevantUsers: RelevantUsers,
): CommentAuthor | null {
  if (user) {
    return { kind: "user", id: Number(user.id), name: user.name };
  }

  if (userId == null) {
    return null;
  }

  // Users outside the viewer's organizations are hidden by the users row filter.
  // Kept, because a thread with no comments left renders no card, only its pin.
  return {
    kind: "user",
    id: Number(userId),
    name: relevantUsers.get(Number(userId))?.name ?? UNKNOWN_AUTHOR_NAME,
  };
}

/** Shown in place of an author the viewer isn't permitted to read. */
const UNKNOWN_AUTHOR_NAME = "Unknown user";

/**
 * Applies the rail's filters to a thread list.
 *
 * "Yours" is authorship of any comment in the thread, not just the opening one:
 * a thread you replied to is one you're part of, and dropping it would hide a
 * conversation you're in.
 */
export function filterThreads<Thread extends FilterableThread & { id: string }>(
  threads: readonly Thread[],
  filters: CommentFilters,
  currentUserId: number | undefined,
  joinedAt?: string | null,
  /**
   * A thread to keep regardless of the filters — the one currently open.
   * Opening a thread marks it read, which with "only unread" on would drop it
   * from the list under the reader and close the card they just opened.
   */
  keepThreadId?: string | null,
): Thread[] {
  return threads.filter((thread) => {
    // Ahead of the open thread's reprieve, and the only filter that is:
    // resolving is a request to be done with it, so it goes on that click. A
    // caller opening a resolved thread turns "show resolved" on instead.
    if (!filters.showResolved && isThreadResolved(thread)) {
      return false;
    }

    if (keepThreadId && thread.id === keepThreadId) {
      return true;
    }

    if (filters.onlyMine && !isAuthoredBy(thread, currentUserId)) {
      return false;
    }

    if (filters.onlyUnread && !isThreadUnread(thread, joinedAt)) {
      return false;
    }

    return true;
  });
}

/**
 * Hides threads started after the viewed render was superseded.
 *
 * Upper bound only — comments carry forward, so one left on v1 still shows on
 * v3. Judged per thread rather than per comment: filtering individual comments
 * would leave a canvas pin whose card is empty.
 */
export function filterThreadsToVersion<
  Thread extends { created_at: string; comments: readonly unknown[] },
>(threads: readonly Thread[], cutoff: string | null): readonly Thread[] {
  if (!cutoff) {
    return threads;
  }

  const supersededAt = new Date(cutoff).getTime();

  return threads.filter(
    (thread) => new Date(thread.created_at).getTime() < supersededAt,
  );
}

/**
 * Signed-out or still-loading viewers own nothing, so the filter matches no
 * thread rather than silently matching every one.
 */
function isAuthoredBy(
  thread: FilterableThread,
  currentUserId: number | undefined,
): boolean {
  if (currentUserId === undefined) {
    return false;
  }

  return thread.comments.some(
    (comment) => Number(comment.author_user_id) === currentUserId,
  );
}

/** Each group together, in the order its first item came, and each item in its own order within it. */
export function groupInOrder<T>(
  items: readonly T[],
  groupOf: (item: T) => string | null,
): T[] {
  const groups = new Map<string | null, T[]>();
  for (const item of items) {
    const key = groupOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.values()].flat();
}
